import { useContext, createContext, useMemo } from 'react'

import {
  useGetHasUnreadConversationsQuery,
  usePersonalEventSubscription,
} from '@/views/BuyerSellerChat/operations.generated'

import { useUser } from '../contexts/user/User.context'

import type { ReactNode } from 'react'

type UserEventProviderProps = {
  children: ReactNode
}

type UserEventContext = {
  hasNotifications: boolean
  refetchNotifications: () => void
}

const UserEventContext = createContext<UserEventContext>({
  hasNotifications: false,
  refetchNotifications: () => {},
})

export const UserEventProvider = ({ children }: UserEventProviderProps) => {
  const { isConnected } = useUser()
  const { data, refetch } = useGetHasUnreadConversationsQuery({ fetchPolicy: 'network-only', skip: !isConnected })
  const { id } = data?.viewer || {}

  usePersonalEventSubscription({
    skip: !data?.viewer,
    onData({ client, data: subscriptionData }) {
      const data =
        subscriptionData.data?.personalEvent.__typename === 'PersonalEventConversationNewMessage'
          ? subscriptionData.data.personalEvent
          : null

      if (!data) {
        return
      }

      const isOnConversationDetailPage = location.search.includes('conversationId=' + data.message.conversationId)
      const isAuthor = data.message.author.id === id

      const conversationRef = client.cache.identify({
        __typename: 'Conversation',
        id: data.message.conversationId,
      })

      client.cache.modify({
        id: conversationRef,
        fields: {
          lastMessage: () => ({
            __ref: 'ConversationMessageText:' + data.message.id,
          }),
          hasUnreadMessages: () => !isOnConversationDetailPage && !isAuthor,
        },
      })
      if (!isOnConversationDetailPage || !isAuthor) {
        const conversationRef = client.cache.identify({
          __typename: 'User',
          id,
        })

        client.cache.modify({
          id: conversationRef,
          fields: {
            hasUnreadConversations: () => true,
          },
        })
      }
    },
  })

  const value: UserEventContext = useMemo(
    () => ({
      hasNotifications: data?.viewer?.hasUnreadConversations || false,
      refetchNotifications: refetch,
    }),
    [data?.viewer?.hasUnreadConversations]
  )

  return <UserEventContext.Provider value={value}>{children}</UserEventContext.Provider>
}

export const useUserEvent = () => useContext(UserEventContext)
